import { useState } from "react";
import { Outlet, useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client";
import usePageTitle from "@/hooks/usePageTitle";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import AppLayout from "@/layouts/AppLayout";
import Breadcrumb from "@/components/Breadcrumb";
import ObjectHeading from "@/components/ObjectHeading";
import Tag from "@/components/Tag";
import { ProfileSkeleton } from "@/components/Skeleton";
import EmptyState from "@/components/EmptyState";
import { toFormat } from "@/lib/dateHelpers";
import { scrollToElement } from "@/lib/scrollHelpers";
import { usePinnedNotes } from "@/features/global/components/NotesTable";
import { PERSON_NOTES_QUERY } from "@/pages/people/[pgaId]/person/notes/queries";
import { STAGE_LABELS, APPLICATION_TYPE_LABELS, UNASSIGNED_REVIEWER_LABEL } from "../types";
import type { ApplicationStage, ApplicationType } from "../types";
import {
  MEMBERSHIP_APPLICATION_QUERY,
  type MembershipApplicationQueryResponse,
  type MembershipApplicationQueryVariables
} from "./queries";
import { canApproveApplication, canDenyApplication } from "./utils";
import ApplicationApprovedDialog from "./_components/ApplicationApprovedDialog";
import ApproveApplicationDialog from "./_components/ApproveApplicationDialog";
import DenyApplicationDialog from "./_components/DenyApplicationDialog";
import type { RegistrationOrder } from "./types";
import TabLayout from "./_TabLayout";

const stageTagType: Record<string, "surface" | "information" | "warn" | "success" | "error"> = {
  draft: "surface",
  submitted: "surface",
  under_review: "information",
  changes_requested: "warn",
  approved: "success",
  denied: "error"
};

export default function ApplicationDetailLayout() {
  useRequireCapability({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageApplications
  });
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [denyDialogOpen, setDenyDialogOpen] = useState(false);
  const [approvedOrders, setApprovedOrders] = useState<{
    registrationOrder: RegistrationOrder;
    upcomingDuesOrder?: RegistrationOrder | null;
  } | null>(null);

  const { data, loading } = useQuery<MembershipApplicationQueryResponse, MembershipApplicationQueryVariables>(
    MEMBERSHIP_APPLICATION_QUERY,
    {
      variables: { id: applicationId! },
      skip: !applicationId
    }
  );

  const application = data?.membershipApplication;
  const pgaId = application?.person?.pgaId;
  const pageTitle = application ? `${application.firstName ?? ""} ${application.lastName ?? ""}`.trim() : undefined;

  usePageTitle("/people/membership-services/applications/:applicationId", pageTitle);

  const { data: notesData } = useQuery(PERSON_NOTES_QUERY, {
    variables: {
      pgaId,
      input: {
        notableType: "MEMBERSHIP_APPLICATION",
        notableId: applicationId
      }
    },
    skip: !pgaId || !applicationId
  });

  const allNotes = notesData?.personNotes?.nodes ?? [];

  const notesTabPath = `/people/membership-services/applications/${applicationId}/notes`;
  const isOnNotesTab = location.pathname.replace(/\/$/, "") === notesTabPath;

  const navigateToNote = (noteId?: string) => {
    if (isOnNotesTab) {
      scrollToElement("notes-table");
    } else {
      const path = noteId ? `${notesTabPath}?highlightNote=${noteId}` : notesTabPath;
      navigate(path);
    }
  };

  const { pinnedNotes, shouldAutoOpenNotes } = usePinnedNotes({
    notes: allNotes,
    entityId: applicationId || "",
    sessionStorageKey: "visitedApplicationDetails"
  });

  const applicationPinnedNotes = pinnedNotes
    ? {
        ...pinnedNotes,
        onViewAll: () => navigateToNote(),
        getNoteAction: (note: { id: string }) => () => navigateToNote(note.id)
      }
    : undefined;

  const status = application?.status as ApplicationStage | undefined;
  const appType = application?.applicationType as ApplicationType | undefined;
  const reviewerName = application?.reviewer?.name ?? UNASSIGNED_REVIEWER_LABEL;
  const contextValue = { application: application! };

  const canDeny = !!application && canDenyApplication(application);
  const canApprove = !!application && canApproveApplication(application);

  const actionButtons =
    canDeny || canApprove
      ? [
          ...(canDeny
            ? [
                {
                  text: "Deny Application",
                  onClick: () => setDenyDialogOpen(true),
                  testId: "deny-application-button",
                  variant: "outlined" as const
                }
              ]
            : []),
          ...(canApprove
            ? [
                {
                  text: "Approve Application",
                  onClick: () => setApproveDialogOpen(true),
                  testId: "approve-application-button",
                  variant: "filled" as const
                }
              ]
            : [])
        ]
      : undefined;

  return (
    <AppLayout interior bottomBar>
      <Breadcrumb
        title="Applications"
        compactTitle="Application"
        backLink={{ to: "/people/membership-services/applications/all" }}
      />
      {loading && <ProfileSkeleton />}
      {!loading && !application && (
        <EmptyState
          iconName="description"
          title="Application Not Found"
          caption="The application you're looking for doesn't exist or may have been removed."
        />
      )}
      {application && (
        <>
          <ObjectHeading
            title={`${application.firstName ?? ""} ${application.lastName ?? ""}`.trim() || "Unknown Applicant"}
            imageUrl={application.profilePhotoUrl ?? undefined}
            iconName="person"
            isDetailsView
            pinnedNotes={applicationPinnedNotes}
            initialNotesDialogOpen={shouldAutoOpenNotes}
            actions={actionButtons}
            tag={
              status
                ? [
                    <Tag key="stage" tagType={stageTagType[status] ?? "surface"} tagStyle="strong" size="small">
                      {STAGE_LABELS[status] ?? status}
                    </Tag>
                  ]
                : undefined
            }
            metaData={[
              { text: application.person.pgaId, label: "PGA ID" },
              {
                text: appType ? (APPLICATION_TYPE_LABELS[appType] ?? appType) : "--",
                label: "Application Type"
              },
              {
                text: application.submittedAt ? (toFormat(application.submittedAt, "MMM dd, yyyy") ?? "--") : "--",
                label: "Submitted"
              },
              { text: reviewerName, label: "Reviewer" }
            ]}
          />
          <ApproveApplicationDialog
            open={approveDialogOpen}
            onClose={() => setApproveDialogOpen(false)}
            applicationId={application.id}
            applicationType={appType}
            onApproved={(orders) => setApprovedOrders(orders)}
          />
          {approvedOrders ? (
            <ApplicationApprovedDialog
              open
              applicationId={application.id}
              registrationOrder={approvedOrders.registrationOrder}
              upcomingDuesOrder={approvedOrders.upcomingDuesOrder}
              onClose={() => setApprovedOrders(null)}
            />
          ) : null}
          <DenyApplicationDialog
            open={denyDialogOpen}
            onClose={() => setDenyDialogOpen(false)}
            applicationId={application.id}
          />
          <TabLayout>
            <Outlet context={contextValue} />
          </TabLayout>
        </>
      )}
    </AppLayout>
  );
}
